import { Plus } from "lucide-react";
import { Outlet } from "react-router-dom";
import { useContas } from "@/api/consultas";
import { Botao } from "@/componentes/Botao";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { useModalNaUrl } from "@/componentes/useModalNaUrl";
import { useSessao } from "@/sessao/useSessao";
import { ContaComConvenios } from "./ContaComConvenios";
import { EditarConta } from "./EditarConta";
import { NovaConta } from "./NovaConta";
import { NovoConvenio } from "./NovoConvenio";
import { pendenciaOferece } from "./pendencias";
import { useCedenteDaRota } from "./useCedenteDaRota";

/*
 * `/cedentes/:id/contas`: as contas do cedente, com os convênios de cada uma.
 *
 * A aba lê `GET /pessoas/{id}/contas`, e não os convênios: conta sem convênio precisa
 * aparecer, porque é dela que sai o próximo passo.
 *
 * O `Outlet` é o painel do convênio: rota filha, com a aba montada embaixo e a mesma
 * rolagem (design D1). Os modais mostram o combinado do `?abrir=`, e o papel é conferido
 * antes de montar, porque a URL qualquer um cola. `NovoConvenio` é do grupo 12, e a
 * montagem dele aqui fica como está.
 *
 * Quem diz `Sem conta` e oferece `Nova conta` é a lista de pendências, logo acima: a aba
 * não repete. A resposta espera a leitura do cedente: as contas chegam primeiro, e o vazio
 * apareceria para sumir em seguida. A linha guarda a altura enquanto isso.
 */
export function AbaDeContas() {
  const { id, pessoa, prontidao, consulta } = useCedenteDaRota();
  const { cliente, pode } = useSessao();
  const modal = useModalNaUrl();
  const ehAdmin = pode("admin");

  const leitura = useContas(cliente, id);
  const contas = leitura.data?.contas ?? [];

  // Sem o cedente, a faixa é da página, e duas diriam a mesma coisa. Espera a leitura dele
  // terminar: as contas respondem primeiro, e a faixa da aba apareceria para sumir em seguida.
  const falhou = leitura.error !== null && consulta.isSuccess;

  const decidida = !consulta.isPending && !pendenciaOferece(prontidao, "Nova conta");
  const vazia = decidida && leitura.isSuccess && contas.length === 0;
  const comBotao = decidida && ehAdmin;

  return (
    <>
      <div aria-busy={leitura.isPending} className="flex flex-col gap-3">
        {falhou && <FaixaDeErro erro={leitura.error} />}

        {(consulta.isPending || vazia || comBotao) && (
          <div className="flex min-h-8 items-center gap-3">
            {vazia && <p className="text-texto-2">Nenhuma conta.</p>}
            {comBotao && (
              <Botao className="ml-auto" onClick={() => modal.abrirModal({ abrir: "nova-conta" })}>
                <Plus aria-hidden="true" className="size-4" />
                Nova conta
              </Botao>
            )}
          </div>
        )}

        {leitura.isPending && <Esqueleto className="h-[172px]" />}

        {contas.length > 0 && (
          <ul aria-label="Contas" className="flex flex-col gap-3">
            {contas.map((conta) => (
              <ContaComConvenios
                key={conta.conta_id}
                pessoaID={id}
                conta={conta}
                ehAdmin={ehAdmin}
                semConvenioNaLista={pendenciaOferece(prontidao, "Novo convênio", conta.conta_id)}
                aoEditar={() =>
                  modal.abrirModal({ abrir: "editar-conta", conta: conta.conta_id })
                }
                aoNovoConvenio={() =>
                  modal.abrirModal({ abrir: "novo-convenio", conta: conta.conta_id })
                }
              />
            ))}
          </ul>
        )}
      </div>

      {modal.abrir === "nova-conta" && ehAdmin && pessoa !== undefined && (
        <NovaConta pessoaID={id} pessoaNome={pessoa.razao_social} aoFechar={modal.fechar} />
      )}

      {modal.abrir === "editar-conta" &&
        modal.conta !== null &&
        ehAdmin &&
        pessoa !== undefined && (
          <EditarConta
            pessoaID={id}
            pessoaNome={pessoa.razao_social}
            contaID={modal.conta}
            aoFechar={modal.fechar}
          />
        )}

      {modal.abrir === "novo-convenio" && modal.conta !== null && ehAdmin && (
        <NovoConvenio pessoaID={id} contaID={modal.conta} aoFechar={modal.fechar} />
      )}

      <Outlet />
    </>
  );
}
